import React, { useState } from 'react'
import { Card, CardHeader, CardBody, Button } from './ui'
import { mockApi } from '../utils/mockApi'

export const MockApiTester: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [testResults, setTestResults] = useState<string[]>([])

  const addResult = (message: string) => {
    setTestResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`])
  }

  const clearResults = () => {
    setTestResults([])
  }

  const testLogin = async () => {
    try {
      addResult('Testing login...')
      await mockApi.login('admin@workbeam.com', 'password123')
      addResult('✅ Login test passed')
    } catch (error: any) {
      addResult(`❌ Login test failed: ${error.message}`)
    }
  }

  const testInvalidLogin = async () => {
    try {
      addResult('Testing invalid login...')
      await mockApi.login('invalid@email.com', 'wrongpassword')
      addResult('❌ Invalid login test failed (should have thrown error)')
    } catch (error: any) {
      addResult(`✅ Invalid login test passed: ${error.message}`)
    }
  }

  const testForgotPassword = async () => {
    try {
      addResult('Testing forgot password...')
      await mockApi.forgotPassword('admin@workbeam.com')
      addResult('✅ Forgot password test passed')
    } catch (error: any) {
      addResult(`❌ Forgot password test failed: ${error.message}`)
    }
  }

  const testResetPassword = async () => {
    try {
      addResult('Testing password reset...')
      await mockApi.resetPassword('valid-reset-token-123', 'NewPassword123')
      addResult('✅ Password reset test passed')
    } catch (error: any) {
      addResult(`❌ Password reset test failed: ${error.message}`)
    }
  }

  const testNetworkError = async () => {
    try {
      addResult('Testing network error...')
      await mockApi.simulateError('network')
      addResult('❌ Network error test failed (should have thrown error)')
    } catch (error: any) {
      addResult(`✅ Network error test passed: ${error.message}`)
    }
  }

  const testServerError = async () => {
    try {
      addResult('Testing server error...')
      await mockApi.simulateError('server')
      addResult('❌ Server error test failed (should have thrown error)')
    } catch (error: any) {
      addResult(`✅ Server error test passed: ${error.message}`)
    }
  }

  const testTimeoutError = async () => {
    try {
      addResult('Testing timeout error...')
      await mockApi.simulateError('timeout')
      addResult('❌ Timeout error test failed (should have thrown error)')
    } catch (error: any) {
      addResult(`✅ Timeout error test passed: ${error.message}`)
    }
  }

  const clearMockData = () => {
    mockApi.clearMockData()
    addResult('🗑️ Mock data cleared')
  }

  const runAllTests = async () => {
    clearResults()
    addResult('🚀 Starting all tests...')
    
    await testLogin()
    await testInvalidLogin()
    await testForgotPassword()
    await testResetPassword()
    await testNetworkError()
    await testServerError()
    await testTimeoutError()
    
    addResult('🏁 All tests completed!')
  }

  if (!mockApi.isMockEnabled()) {
    return null
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {!isVisible && (
        <button
          onClick={() => setIsVisible(true)}
          className="bg-primary-600 text-white p-3 rounded-full shadow-lg hover:bg-primary-700 transition-colors"
          title="Mock API Tester"
        >
          🔧
        </button>
      )}

      {isVisible && (
        <Card className="w-96 max-h-96 overflow-hidden">
          <CardHeader className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Mock API Tester</h3>
            <button
              onClick={() => setIsVisible(false)}
              className="text-neutral-400 hover:text-neutral-600"
            >
              ✕
            </button>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={testLogin}>
                Test Login
              </Button>
              <Button size="sm" onClick={testInvalidLogin}>
                Test Invalid Login
              </Button>
              <Button size="sm" onClick={testForgotPassword}>
                Test Forgot Password
              </Button>
              <Button size="sm" onClick={testResetPassword}>
                Test Reset Password
              </Button>
              <Button size="sm" onClick={testNetworkError}>
                Test Network Error
              </Button>
              <Button size="sm" onClick={testServerError}>
                Test Server Error
              </Button>
              <Button size="sm" onClick={testTimeoutError}>
                Test Timeout Error
              </Button>
            </div>

            <div className="flex gap-2">
              <Button size="sm" onClick={runAllTests} className="flex-1">
                Run All Tests
              </Button>
              <Button size="sm" onClick={clearMockData} variant="outline">
                Clear Data
              </Button>
              <Button size="sm" onClick={clearResults} variant="outline">
                Clear Log
              </Button>
            </div>

            <div className="border rounded p-2 bg-neutral-50 max-h-32 overflow-y-auto">
              <div className="text-xs space-y-1">
                {testResults.length === 0 ? (
                  <p className="text-neutral-500">No test results yet...</p>
                ) : (
                  testResults.map((result, index) => (
                    <div key={index} className="font-mono">
                      {result}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="text-xs text-neutral-500">
              <p>🔧 Mock API is enabled</p>
              <p>📋 Check console for detailed logs</p>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  )
}
